import type { DnevnikPoste as DnevnikTip, ZapisPoste } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import { datum } from '../lib/format';
import { PreizkusPoste } from '../sestavni/PreizkusPoste';

const STANJE: Record<ZapisPoste['status'], string> = {
  poslano: 'poslano',
  napaka: 'ni uspelo',
  'ni-nastavljeno': 'pošiljanje ni nastavljeno',
};

/** Datum in ura zapisa; ura je pri iskanju vzroka pomembna. */
function trenutek(iso: string): string {
  const cas = new Date(iso);
  return `${datum(iso)} ob ${cas.toLocaleTimeString('sl-SI', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * Dnevnik pošiljanj (samo skrbnik).
 *
 * Tu so tehnične podrobnosti — HTTP status, `code`, `message`, `request-id` —
 * ki pri uporabniku nimajo kaj iskati. Stran se odpre takrat, kadar nekaj ne
 * dela. Obravnavan neuspeh ostane zapisan, a izgine iz opozoril.
 */
export function DnevnikPoste(): JSX.Element {
  const [parametri, nastaviParametre] = useSearchParams();
  const zaUporabnika = parametri.get('uporabnik');
  const samoNeuspeli = parametri.get('samoNeuspeli') === '1';

  const [dnevnik, setDnevnik] = useState<DnevnikTip | null>(null);
  const [odprt, setOdprt] = useState<number | null>(
    parametri.get('zapis') !== null ? Number(parametri.get('zapis')) : null,
  );
  const [napaka, setNapaka] = useState<string | null>(null);

  const osvezi = useCallback((): void => {
    api
      .dnevnikPoste({
        samoNeuspeli,
        ...(zaUporabnika !== null ? { uporabnik: zaUporabnika } : {}),
      })
      .then(setDnevnik)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Dnevnika ni bilo mogoče prebrati.');
      });
  }, [samoNeuspeli, zaUporabnika]);

  useEffect(osvezi, [osvezi]);

  const obravnavaj = (id: number): void => {
    api
      .obravnavanoPosiljanje(id)
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Označitve ni bilo mogoče shraniti.');
      });
  };

  const preklopiFilter = (): void => {
    const novi = new URLSearchParams(parametri);
    if (samoNeuspeli) novi.delete('samoNeuspeli');
    else novi.set('samoNeuspeli', '1');
    nastaviParametre(novi);
  };

  return (
    <main>
      <h1>Pošiljanja</h1>
      <p className="podnaslov">
        Dnevnik poslane pošte s podrobnostmi odgovora Microsofta.
        {dnevnik !== null && dnevnik.neobravnavanih > 0 && (
          <> Neobravnavanih neuspehov: {dnevnik.neobravnavanih}.</>
        )}
      </p>

      {napaka !== null && <p className="napaka">{napaka}</p>}

      <PreizkusPoste />

      <section className="kartica">
        <div className="gumbi">
          <h2>Zapisi</h2>
          <label className="stikalo">
            <input type="checkbox" checked={samoNeuspeli} onChange={preklopiFilter} />
            Samo neuspeli
          </label>
          {zaUporabnika !== null && (
            <button
              type="button"
              className="povezava"
              onClick={() => {
                const novi = new URLSearchParams(parametri);
                novi.delete('uporabnik');
                nastaviParametre(novi);
              }}
            >
              Pokaži vse uporabnike
            </button>
          )}
        </div>

        {dnevnik === null && <p>Berem …</p>}
        {dnevnik !== null && dnevnik.zapisi.length === 0 && <p>Zapisov ni.</p>}

        {dnevnik !== null && dnevnik.zapisi.length > 0 && (
          <ul className="seznam dnevnik">
            {dnevnik.zapisi.map((z) => (
              <li key={z.id}>
                <div>
                  <div>
                    {z.za} <span className="drobno">· {z.vrsta}</span>
                  </div>
                  <div className="drobno">
                    {trenutek(z.ob)} · {STANJE[z.status]}
                    {z.obravnavanoOb !== null && (
                      <> · obravnaval {z.obravnaval ?? 'skrbnik'} {datum(z.obravnavanoOb)}</>
                    )}
                  </div>
                  {odprt === z.id && z.napaka !== null && <pre className="surovo">{z.napaka}</pre>}
                </div>
                <div className="dnevnik-gumbi">
                  {z.napaka !== null && (
                    <button
                      type="button"
                      className="povezava"
                      onClick={() => {
                        setOdprt(odprt === z.id ? null : z.id);
                      }}
                    >
                      {odprt === z.id ? 'Skrij podrobnosti' : 'Podrobnosti'}
                    </button>
                  )}
                  {z.status === 'napaka' && z.obravnavanoOb === null && (
                    <button
                      type="button"
                      className="povezava"
                      onClick={() => {
                        obravnavaj(z.id);
                      }}
                    >
                      Označi kot obravnavano
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
